import { useCallback, useEffect, useMemo, useState } from "react";

/* ==========================================================
   CONFIG  (addResort.jsx / Resortrooms.jsx wali same API_BASE rakho)
========================================================== */
const API_BASE = "https://bmtvendorvendorfinal.onrender.com";
const RESORT_API = `${API_BASE}/api/resorts`;
const ROOM_API = `${API_BASE}/api/resort-rooms`;
const UNIT_API = `${API_BASE}/api/resort-room-units`;

const STATUSES = [
  ["AVAILABLE", "Available", "#15803d", "#f0fdf4"],
  ["OCCUPIED", "Occupied", "#b45309", "#fffbeb"],
  ["CLEANING", "Cleaning", "#0369a1", "#f0f9ff"],
  ["MAINTENANCE", "Maintenance", "#6d28d9", "#f5f3ff"],
  ["BLOCKED", "Blocked", "#b91c1c", "#fef2f2"],
  ["INACTIVE", "Inactive", "#4b5563", "#f3f4f6"],
];
const statusMeta = (s) => STATUSES.find((x) => x[0] === s) || STATUSES[5];

const EMPTY_FORM = {
  roomCategoryId: "",
  roomNumber: "",
  bulkNumbers: "",
  floor: "",
  building: "",
  status: "AVAILABLE",
  isActive: true,
  notes: "",
};

/* ==========================================================
   HELPERS
========================================================== */
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("vendorToken") || ""}` });

async function api(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  if (res.status === 401 && data.code === "TOKEN_EXPIRED") {
    localStorage.removeItem("vendorToken");
    localStorage.removeItem("vendorUser");
    window.location.href = "/vendor-login";
  }
  if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

/* "101-110, 201, A12" -> ["101",...,"110","201","A12"]  (max 200) */
function parseRoomNumbers(text) {
  const out = [];
  String(text || "")
    .split(/[,\n]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .forEach((t) => {
      const m = t.match(/^(\d+)\s*-\s*(\d+)$/);
      if (m) {
        const from = Number(m[1]);
        const to = Number(m[2]);
        const width = m[1].length;
        if (to >= from && to - from < 200) {
          for (let n = from; n <= to; n += 1) out.push(String(n).padStart(width, "0"));
          return;
        }
      }
      out.push(t);
    });
  return [...new Set(out.map((n) => n.toUpperCase()))].slice(0, 200);
}

/* ==========================================================
   SMALL UI PIECES
========================================================== */
function Input({ label, value, onChange, type = "text", placeholder, required, hint, ...rest }) {
  return (
    <div className="bmt-field-group">
      <label>{label}{required && " *"}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} {...rest} />
      {hint && <small style={{ color: "#6b7280" }}>{hint}</small>}
    </div>
  );
}

function Notice({ kind, children }) {
  if (!children) return null;
  const bad = kind === "error";
  return (
    <div
      role={bad ? "alert" : "status"}
      style={{
        padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 14,
        background: bad ? "#fef2f2" : "#f0fdf4", color: bad ? "#991b1b" : "#166534",
        border: `1px solid ${bad ? "#fecaca" : "#bbf7d0"}`,
      }}
    >
      {children}
    </div>
  );
}

/* ==========================================================
   MAIN COMPONENT
========================================================== */
export default function ResortRoomUnits() {
  const [resorts, setResorts] = useState([]);
  const [resortId, setResortId] = useState(() => sessionStorage.getItem("bmtResortUnitsResort") || "");
  const [loadingResorts, setLoadingResorts] = useState(true);

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [view, setView] = useState("list"); // list | form
  const [editingId, setEditingId] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const setTop = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  /* ---------- LOAD RESORTS ---------- */
  useEffect(() => {
    (async () => {
      try {
        const data = await api(`${RESORT_API}/my-resorts`);
        const list = Array.isArray(data.resorts) ? data.resorts : [];
        setResorts(list);
        setResortId((cur) => (list.some((r) => r._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingResorts(false);
      }
    })();
  }, []);

  /* ---------- LOAD CATEGORIES + UNITS ---------- */
  const loadAll = useCallback(async () => {
    if (!resortId) { setUnits([]); setCategories([]); return; }
    setLoadingUnits(true);
    try {
      const [roomsRes, unitsRes] = await Promise.all([
        api(`${ROOM_API}/resort/${resortId}`),
        api(`${UNIT_API}/resort/${resortId}`),
      ]);
      setCategories(Array.isArray(roomsRes.rooms) ? roomsRes.rooms : []);
      setUnits(Array.isArray(unitsRes.roomUnits) ? unitsRes.roomUnits : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingUnits(false);
    }
  }, [resortId]);

  useEffect(() => {
    if (resortId) sessionStorage.setItem("bmtResortUnitsResort", resortId);
    setCategoryFilter("ALL");
    setStatusFilter("ALL");
    loadAll();
  }, [resortId, loadAll]);

  const resortName = resorts.find((r) => r._id === resortId)?.name || "";
  const catId = (u) => u.roomCategory?._id || u.roomCategory;
  const catName = (u) => u.roomCategory?.name || u.roomCategory?.title || categories.find((c) => c._id === catId(u))?.name || "—";

  /* ---------- DERIVED ---------- */
  const counts = useMemo(() => {
    const c = {};
    STATUSES.forEach(([k]) => { c[k] = units.filter((u) => u.status === k).length; });
    return c;
  }, [units]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return units.filter((u) => {
      if (categoryFilter !== "ALL" && catId(u) !== categoryFilter) return false;
      if (statusFilter !== "ALL" && u.status !== statusFilter) return false;
      if (!q) return true;
      return [u.roomNumber, u.floor, u.building, u.notes].some((v) => String(v || "").toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, search, categoryFilter, statusFilter]);

  const bulkPreview = useMemo(() => parseRoomNumbers(form.bulkNumbers), [form.bulkNumbers]);

  /* ---------- OPEN FORM ---------- */
  const startNew = () => {
    setForm({ ...EMPTY_FORM, roomCategoryId: categoryFilter !== "ALL" ? categoryFilter : categories[0]?._id || "" });
    setEditingId(null);
    setBulkMode(false);
    setError("");
    setSuccess("");
    setView("form");
  };

  const startEdit = (u) => {
    setForm({
      roomCategoryId: catId(u) || "",
      roomNumber: u.roomNumber || "",
      bulkNumbers: "",
      floor: u.floor || "",
      building: u.building || "",
      status: u.status || "AVAILABLE",
      isActive: u.isActive !== false,
      notes: u.notes || "",
    });
    setEditingId(u._id);
    setBulkMode(false);
    setError("");
    setSuccess("");
    setView("form");
  };

  /* ---------- SAVE ---------- */
  const save = async () => {
    setError("");
    setSuccess("");

    if (editingId) {
      if (!form.roomNumber.trim()) return setError("Room number is required.");
      setSaving(true);
      try {
        await api(`${UNIT_API}/${editingId}`, {
          method: "PUT",
          body: JSON.stringify({
            roomNumber: form.roomNumber.trim(),
            floor: form.floor.trim(),
            building: form.building.trim(),
            status: form.status,
            isActive: form.isActive,
            notes: form.notes.trim(),
          }),
        });
        await loadAll();
        setView("list");
        setSuccess("Room unit updated.");
      } catch (e) {
        setError(e.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // ADD (single ya multiple)
    if (!form.roomCategoryId) return setError("Choose a room category first.");
    const numbers = bulkMode ? bulkPreview : [form.roomNumber.trim().toUpperCase()].filter(Boolean);
    if (!numbers.length) return setError(bulkMode ? "Enter at least one room number." : "Room number is required.");

    setSaving(true);
    let created = 0;
    const failed = [];

    // Backend ek request me ek room banata hai, isliye ek-ek karke
    for (const roomNumber of numbers) {
      try {
        await api(UNIT_API, {
          method: "POST",
          body: JSON.stringify({
            resortId,
            roomCategoryId: form.roomCategoryId,
            roomNumber,
            floor: form.floor.trim(),
            building: form.building.trim(),
            notes: form.notes.trim(),
          }),
        });
        created += 1;
      } catch (e) {
        failed.push(`${roomNumber} (${e.message})`);
      }
    }

    await loadAll();
    setSaving(false);

    if (failed.length === 0) {
      setView("list");
      setSuccess(created === 1 ? "Room unit added." : `${created} room units added.`);
    } else if (created > 0) {
      setView("list");
      setSuccess(`${created} room units added.`);
      setError(`${failed.length} could not be added: ${failed.join(", ")}`);
    } else {
      setError(`Nothing was added. ${failed.join(", ")}`);
    }
  };

  /* ---------- QUICK UPDATE (status / active) ---------- */
  const quickUpdate = async (u, patch) => {
    const prev = units;
    setUnits((list) => list.map((x) => (x._id === u._id ? { ...x, ...patch } : x)));
    setError("");
    try {
      await api(`${UNIT_API}/${u._id}`, { method: "PUT", body: JSON.stringify(patch) });
    } catch (e) {
      setUnits(prev);
      setError(e.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete room "${u.roomNumber}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api(`${UNIT_API}/${u._id}`, { method: "DELETE" });
      setUnits((list) => list.filter((x) => x._id !== u._id));
      setSuccess(`Room ${u.roomNumber} deleted.`);
    } catch (e) {
      setError(e.message);
    }
  };

  /* ==========================================================
     NO RESORT YET
  ========================================================== */
  if (!loadingResorts && resorts.length === 0) {
    return (
      <div className="bmt-premium-page">
        <div className="bmt-section-intro">
          <div><h2>Room Units</h2><p>Manage each physical room with its own room number and status.</p></div>
        </div>
        <Notice kind="error">{error}</Notice>
        <section className="bmt-form-card">
          <div className="bmt-empty-state">
            <h3>Add a resort first</h3>
            <p>Room units belong to a resort. Open My Resorts from the menu and add your resort, then come back here.</p>
          </div>
        </section>
      </div>
    );
  }

  /* ==========================================================
     LIST VIEW
  ========================================================== */
  if (view === "list") {
    return (
      <div className="bmt-premium-page">
        <div className="bmt-section-intro">
          <div>
            <h2>Room Units</h2>
            <p>Add each physical room (101, 102...) under a category and track whether it is available, occupied or under maintenance.</p>
          </div>
          <button className="bmt-primary-btn" onClick={startNew} disabled={!resortId || categories.length === 0}>
            + Add Room Units
          </button>
        </div>

        <Notice kind="error">{error}</Notice>
        <Notice kind="success">{success}</Notice>

        <section className="bmt-form-card">
          <div className="bmt-form-grid">
            <div className="bmt-field-group">
              <label>Resort</label>
              <select
                value={resortId}
                onChange={(e) => { setResortId(e.target.value); setSuccess(""); setError(""); }}
                disabled={loadingResorts}
              >
                {resorts.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </div>
            <div className="bmt-field-group">
              <label>Room category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="ALL">All categories</option>
                {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="bmt-field-group">
              <label>Search</label>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Room number, floor or building" />
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setStatusFilter("ALL")}
              style={{
                padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${statusFilter === "ALL" ? "#111827" : "#e5e7eb"}`,
                background: statusFilter === "ALL" ? "#111827" : "#fff",
                color: statusFilter === "ALL" ? "#fff" : "#374151",
              }}
            >
              All ({units.length})
            </button>
            {STATUSES.map(([key, label, color, bg]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(statusFilter === key ? "ALL" : key)}
                style={{
                  padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${statusFilter === key ? color : "#e5e7eb"}`,
                  background: statusFilter === key ? bg : "#fff",
                  color,
                }}
              >
                {label} ({counts[key]})
              </button>
            ))}
          </div>
        </section>

        <section className="bmt-form-card">
          {loadingUnits ? (
            <p>Loading room units...</p>
          ) : categories.length === 0 ? (
            <div className="bmt-empty-state">
              <h3>No room categories in {resortName || "this resort"}</h3>
              <p>Create a room category first (for example Deluxe Room), then add room numbers under it.</p>
            </div>
          ) : units.length === 0 ? (
            <div className="bmt-empty-state">
              <h3>No room units in {resortName || "this resort"}</h3>
              <p>Add your room numbers. You can add many at once, for example 101-110.</p>
              <button className="bmt-primary-btn" onClick={startNew}>Add room units</button>
            </div>
          ) : filtered.length === 0 ? (
            <p>No rooms match your filters.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720, fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#6b7280", fontSize: 13 }}>
                    {["Room", "Category", "Floor", "Building", "Status", "Active", ""].map((h) => (
                      <th key={h} style={{ padding: "8px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => {
                    const [, , color, bg] = statusMeta(u.status);
                    return (
                      <tr key={u._id} style={{ opacity: u.isActive === false ? 0.6 : 1 }}>
                        <td style={{ padding: "10px", borderBottom: "1px solid #f3f4f6" }}>
                          <strong style={{ fontSize: 15 }}>{u.roomNumber}</strong>
                          {u.notes && <div style={{ fontSize: 12, color: "#6b7280", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={u.notes}>{u.notes}</div>}
                        </td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #f3f4f6" }}>{catName(u)}</td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #f3f4f6" }}>{u.floor || "—"}</td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #f3f4f6" }}>{u.building || "—"}</td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #f3f4f6" }}>
                          <select
                            value={u.status}
                            aria-label={`Status of room ${u.roomNumber}`}
                            onChange={(e) => quickUpdate(u, { status: e.target.value })}
                            style={{ background: bg, color, fontWeight: 600, border: `1px solid ${color}33`, borderRadius: 6, padding: "5px 8px" }}
                          >
                            {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #f3f4f6" }}>
                          <input
                            type="checkbox"
                            checked={u.isActive !== false}
                            aria-label={`Room ${u.roomNumber} active`}
                            onChange={(e) => quickUpdate(u, { isActive: e.target.checked })}
                          />
                        </td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #f3f4f6", whiteSpace: "nowrap", textAlign: "right" }}>
                          <button className="bmt-secondary-btn" style={{ marginRight: 8 }} onClick={() => startEdit(u)}>Edit</button>
                          <button className="bmt-secondary-btn" onClick={() => remove(u)}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  /* ==========================================================
     FORM VIEW
  ========================================================== */
  return (
    <div className="bmt-premium-page">
      <div className="bmt-section-intro">
        <div>
          <h2>{editingId ? "Edit Room Unit" : "Add Room Units"}</h2>
          <p>Resort: <b>{resortName}</b></p>
        </div>
        <button className="bmt-secondary-btn" onClick={() => { setView("list"); setError(""); }}>Back to list</button>
      </div>

      <Notice kind="error">{error}</Notice>

      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Room details</h3></div>

        <div className="bmt-form-grid">
          <div className="bmt-field-group">
            <label>Room category *</label>
            <select
              value={form.roomCategoryId}
              onChange={(e) => setTop("roomCategoryId", e.target.value)}
              disabled={!!editingId}
            >
              <option value="" disabled>Select category</option>
              {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            {editingId && <small style={{ color: "#6b7280" }}>Category cannot be changed after the room is created.</small>}
          </div>

          {editingId && (
            <div className="bmt-field-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => setTop("status", e.target.value)}>
                {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          )}
        </div>

        {!editingId && (
          <div style={{ display: "flex", gap: 8, margin: "4px 0 12px" }}>
            <button type="button" className={bulkMode ? "bmt-secondary-btn" : "bmt-primary-btn"} onClick={() => setBulkMode(false)}>Single room</button>
            <button type="button" className={bulkMode ? "bmt-primary-btn" : "bmt-secondary-btn"} onClick={() => setBulkMode(true)}>Many rooms at once</button>
          </div>
        )}

        {bulkMode && !editingId ? (
          <div className="bmt-field-group">
            <label>Room numbers *</label>
            <textarea
              rows={3}
              value={form.bulkNumbers}
              onChange={(e) => setTop("bulkNumbers", e.target.value)}
              placeholder="101-110, 201-205, VILLA1"
            />
            <small style={{ color: "#6b7280" }}>
              Use commas between numbers. A range like 101-110 adds every room from 101 to 110.
              {bulkPreview.length > 0 && ` ${bulkPreview.length} room${bulkPreview.length > 1 ? "s" : ""} will be added.`}
            </small>
            {bulkPreview.length > 0 && (
              <div className="bmt-chip-wrap" style={{ marginTop: 8 }}>
                {bulkPreview.slice(0, 30).map((n) => <span className="bmt-chip" key={n}>{n}</span>)}
                {bulkPreview.length > 30 && <span className="bmt-chip">+{bulkPreview.length - 30} more</span>}
              </div>
            )}
          </div>
        ) : (
          <div className="bmt-form-grid">
            <Input label="Room number" required value={form.roomNumber} onChange={(v) => setTop("roomNumber", v)} placeholder="e.g. 101 or A-12" />
          </div>
        )}

        <div className="bmt-form-grid">
          <Input label="Floor" value={form.floor} onChange={(v) => setTop("floor", v)} placeholder="e.g. Ground, 1st floor" />
          <Input label="Building / block" value={form.building} onChange={(v) => setTop("building", v)} placeholder="e.g. Main block" />
        </div>

        <div className="bmt-field-group">
          <label>Notes</label>
          <textarea rows={3} value={form.notes} onChange={(e) => setTop("notes", e.target.value)} placeholder="Internal note for staff, e.g. AC being repaired" />
        </div>
      </section>

      {editingId && (
        <section className="bmt-form-card">
          <div className="bmt-form-card-head"><h3>Availability</h3></div>
          <label className={`bmt-check-card ${form.isActive ? "selected" : ""}`} style={{ maxWidth: 360 }}>
            <input type="checkbox" checked={form.isActive} onChange={(e) => setTop("isActive", e.target.checked)} />
            <span>This room can be assigned to bookings</span>
          </label>
        </section>
      )}

      <div className="bmt-form-actions">
        <button className="bmt-secondary-btn" disabled={saving} onClick={() => { setView("list"); setError(""); }}>Cancel</button>
        <button className="bmt-primary-btn" disabled={saving} onClick={save}>
          {saving ? "Saving..." : editingId ? "Save changes" : bulkMode ? `Add ${bulkPreview.length || ""} rooms`.trim() : "Add room unit"}
        </button>
      </div>
    </div>
  );
}